import React from "react";
import { Activity, AlertCircle, Calendar, Cpu, Database, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { Section, KpiCard, InsightsGrid, ChatPanel, ModuleHeader } from "../components/intel/IntelKit";
import useCustomerData from "../hooks/useCustomerData";
import ConnectTallyBanner from "../components/ConnectTallyBanner";

// ---------------------------------------------------------------------------
// SECTION 1 — KPIs
// ---------------------------------------------------------------------------
const KPIS = [
  { id: "accuracy",  label: "Forecast Accuracy",    sub: "30-day rolling",        status: "green", icon: TrendingUp,  realKey: null,                 format: "percentage", tooltip: "Computed after 30 days of forecasts vs actuals. Needs Tally daily sync." },
  { id: "models",    label: "Models Running",       sub: "Revenue · cash · steel", status: "blue",  icon: Cpu,         realKey: null,                 format: "number",     tooltip: "Forecasting models activate after 60 days of clean Tally history." },
  { id: "deviation", label: "Forecast Deviation",   sub: "Above P50 baseline",    status: "amber", icon: Activity,    realKey: null,                 format: "percentage", tooltip: "Calculated from rolling actuals vs P50. Live after first 30 days." },
  { id: "next30",    label: "Next 30-day Revenue",  sub: "P50 projection",        status: "green", icon: TrendingUp,  realKey: "next30Revenue",      format: "currency",   tooltip: "Naive P50 baseline from your most recent month of revenue. Refines as more data lands." },
  { id: "cashdip",   label: "Cash Dip Alert",       sub: "GST + payroll outflow", status: "red",   icon: AlertCircle, realKey: null,                 format: "raw",        tooltip: "Cash-dip detection activates once your bank + Tally sync is complete." },
  { id: "datapts",   label: "Data Points / Day",    sub: "Tally · CRM · bank",    status: "blue",  icon: Database,    realKey: null,                 format: "number",     tooltip: "Counted from active sync sources. Climbs as you connect more data." },
];

function realValueForForecast(realKey, customerData) {
  if (!realKey || !customerData) return null;
  switch (realKey) {
    case "next30Revenue": {
      const rev = Number(customerData?.financial?.revenue_mtd || 0);
      return rev > 0 ? rev : null; // mirror most-recent month as the simple baseline
    }
    default: return null;
  }
}

// ---------------------------------------------------------------------------
// SECTION 2 — Weekly forecast chart placeholder
// ---------------------------------------------------------------------------
function WeeklyForecastChart() {
  return (
    <div data-testid="fcst-chart-empty" className="flex h-[200px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-[12px] text-slate-500">
      <span className="text-slate-400 mr-2 text-[18px] leading-none">—</span>
      Forecast chart appears after 8+ weeks of Tally history.
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — Active forecast models placeholder
// ---------------------------------------------------------------------------
function ModelsTable() {
  return (
    <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-[12px] text-slate-500" data-testid="fcst-models-empty">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Active forecast models</div>
      <div className="mt-2">
        <span className="text-slate-400 text-[18px] leading-none mr-2">—</span>
        Models begin training once you have 8+ weeks of synced Tally data. Connect Tally and let the agent run for a full quarter to unlock revenue, cash, demand and AR forecasts.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 4 — Signal banners placeholder
// ---------------------------------------------------------------------------
function Signals() {
  return (
    <div data-testid="fcst-signals-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
      <span className="text-slate-400 text-[18px] leading-none">—</span>
      <span>Signals fire once your forecast models have enough data. Smart Alerts in the Executive Dashboard already capture every short-term risk from your live Tally feed.</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function Forecasting() {
  const { customerData, dataAvailable, isLoading } = useCustomerData();
  return (
    <div data-testid="fcst-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <ModuleHeader
        overline="Predictive & Forecasting Intelligence"
        title="What's next · 30, 60, 90 days"
        right={<><Calendar size={13} className="text-emerald-600" />Refreshed daily 06:00 IST</>}
      />

      <section data-testid="fcst-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-6">
        {KPIS.map((k) => (
          <KpiCard
            key={k.id}
            kpi={k}
            testidPrefix="fcst-kpi"
            isLoading={isLoading}
            dataAvailable={dataAvailable}
            realValue={realValueForForecast(k.realKey, customerData)}
          />
        ))}
      </section>

      <Section testid="fcst-chart-section" title="Weekly forecast" subtitle="P50 with P25/P75 confidence band · last 12 weeks">
        <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-5">
          <div className="xl:col-span-3"><WeeklyForecastChart /></div>
          <div className="xl:col-span-2"><ModelsTable /></div>
        </div>
      </Section>

      <Section testid="fcst-signals-section" title="Signals firing today" subtitle="Tied to the active forecast models">
        <Signals />
      </Section>

      <InsightsGrid
        subtitle="What your Forecasting advisor would tell you today"
        fetchFn={(force) => api.aiInsightsGenerate("intelligence", api.forecastInsights, force)}
        testidPrefix="fcst"
        dataAvailable={dataAvailable}
      />

      <ChatPanel
        title="Ask your Forecasting advisor"
        subtitle="Cash, demand, prices · Hindi or English"
        placeholder="Ask about cash dips, demand surges, or pricing…"
        sendFn={api.forecastChat}
        testidPrefix="fcst"
        emptyHint="Ask anything forward-looking. Try: 'When will my cash dip below ₹5L?' or 'Project revenue for next quarter.'"
      />
      </div>
    </div>
  );
}
